import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { PERFILS_MOCK, desaPerfilMock, perfilMockDesat } from '@/auth/mockMe';
import { Desplegable } from '@/components/Desplegable';
import { Logotip } from '@/components/Logotip';

/** Logotip oficial de Microsoft (quatre quadrats) per al botó d'inici de sessió. */
function LogoMicrosoft() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="10" height="10" fill="#F25022" />
      <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
      <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
      <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

const esquemaMock = z.object({ perfil: z.enum(PERFILS_MOCK) });
type FormMock = z.infer<typeof esquemaMock>;

function FormulariMock() {
  const { t } = useTranslation('comu');
  const { login } = useAuth();
  const queryClient = useQueryClient();
  const { control, handleSubmit } = useForm<FormMock>({
    resolver: zodResolver(esquemaMock),
    defaultValues: { perfil: perfilMockDesat() },
  });
  const envia = handleSubmit(async ({ perfil }) => {
    desaPerfilMock(perfil);
    queryClient.removeQueries();
    await login();
  });
  return (
    <form onSubmit={(e) => void envia(e)} className="login__form">
      <label className="camp">
        <span className="camp__label">{t('login.perfilMock')}</span>
        <Controller
          control={control}
          name="perfil"
          render={({ field }) => (
            <Desplegable
              aria-label={t('login.perfilMock')}
              valor={field.value}
              onCanvia={field.onChange}
              opcions={PERFILS_MOCK.map((p) => ({ valor: p, etiqueta: t(`perfilsMock.${p}`) }))}
            />
          )}
        />
      </label>
      <button type="submit" className="btn-microsoft">
        <LogoMicrosoft />
        <span>{t('login.boto')}</span>
      </button>
      <p className="t-caption dim center" style={{ margin: 0 }}>
        {t('login.mock')}
      </p>
    </form>
  );
}

export function W01Login() {
  const { t } = useTranslation('comu');
  const { mode, login, carregant } = useAuth();
  return (
    <div className="login" data-screen="W-01">
      <div className="login__card">
        <Logotip mida={88} className="login__logo" />
        <div className="login__marca">{t('login.marca')}</div>
        <h1>{t('login.titolMarca')}</h1>
        <p className="t-subhead dim" style={{ margin: '6px 0 0' }}>
          {t('login.subtitol')}
        </p>
        <div className="panel w-full" style={{ marginTop: 24, textAlign: 'left' }}>
          <div className="panel__body">
            {mode === 'mock' ? (
              <FormulariMock />
            ) : (
              <button
                type="button"
                className="btn-microsoft"
                disabled={carregant}
                onClick={() => void login()}
              >
                <LogoMicrosoft />
                <span>{carregant ? t('sessio.iniciant') : t('login.boto')}</span>
              </button>
            )}
            <p className="t-caption dim center" style={{ margin: '12px 0 0' }}>
              {t('login.peu')}
              <br />
              {t('login.peu2')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
